import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { VisuallyHidden } from 'radix-ui'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { useAuth } from '@/contexts/AuthContext'
import { useFinanceFeatures } from '@/hooks/useFinanceFeatures'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { activeHref, buildNavigation, NavigationPolicy } from '@/lib/navigation'
import { AppSidebar } from './layout/AppSidebar'
import { UserMenu } from './layout/UserMenu'
import { NotificationDropdown } from './notifications/NotificationDropdown'
import { GlobalTimerWidget } from './GlobalTimerWidget'
import { ThemeToggle } from './ThemeToggle'

interface LayoutProps {
  children: ReactNode
}

/** Casca autenticada: menu lateral (gaveta no celular), cabeçalho e conteúdo. */
export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const isDesktop = useIsDesktop()
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const { features } = useFinanceFeatures()

  const model = useMemo(() => buildNavigation(user, features), [user, features])
  const active = activeHref(location.pathname, model)
  const hasInterface = new NavigationPolicy(user, features).hasInterface

  // Desktop: "sidebarOpen" empurra o conteúdo. Celular: a gaveta (Sheet) sobrepõe.
  useEffect(() => {
    if (isDesktop) setMobileNavOpen(false)
  }, [isDesktop])

  // No celular, navegar fecha a gaveta.
  useEffect(() => {
    if (!isDesktop) setMobileNavOpen(false)
  }, [location.pathname, isDesktop])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const toggleMenu = () => (isDesktop ? setSidebarOpen(!sidebarOpen) : setMobileNavOpen(true))

  return (
    <div className="flex min-h-screen bg-muted">
      <aside
        className={`fixed inset-y-0 left-0 z-30 hidden h-full overflow-y-auto border-r border-border bg-background transition-all duration-300 md:block ${
          sidebarOpen ? 'w-64' : 'w-0'
        }`}
      >
        <div className={sidebarOpen ? 'block' : 'hidden'}>
          <AppSidebar model={model} active={active} />
        </div>
      </aside>

      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-64 overflow-y-auto p-0 sm:max-w-xs">
          <VisuallyHidden.Root>
            <SheetTitle>Menu de navegação</SheetTitle>
            <SheetDescription>Links para as seções do sistema</SheetDescription>
          </VisuallyHidden.Root>
          <AppSidebar model={model} active={active} />
        </SheetContent>
      </Sheet>

      {/* min-w-0: conteúdo largo (quadro de leads, tabelas) rola no próprio contêiner
          em vez de alargar a página e empurrar botões para fora da tela no celular. */}
      <div className={`flex min-w-0 flex-1 flex-col transition-all duration-300 ${sidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <header className="sticky top-0 z-20 flex h-16 items-center border-b border-border bg-background px-4 shadow-xs">
          <div className="flex w-full items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMenu}
              aria-label={isDesktop && sidebarOpen ? 'Fechar menu' : 'Abrir menu'}
            >
              {isDesktop && sidebarOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </Button>
            <div className="flex items-center gap-2 sm:gap-4">
              <ThemeToggle />
              <NotificationDropdown />
              <UserMenu user={user} onLogout={handleLogout} />
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          {hasInterface ? children : (
            <p role="alert" className="p-6 text-sm text-muted-foreground">
              Este perfil não usa a interface do sistema (o agente acessa só pela API).
            </p>
          )}
        </main>

      </div>

      {/* Cronômetro do registro de horas: só com faturas/horas ligadas */}
      {features.invoices && <GlobalTimerWidget />}
    </div>
  )
}
